import React, { useContext, useState } from 'react';
import {
    FormGroup,
    FormControl,
    Button,
    Input,
    ColorPicker,
    Alert,
    ButtonSelect
} from 'components';
import styles from 'styles/campaign_settings_second_step.module.scss';
import Bundle from './Bundle';
import { CAMPAIGN_TYPES } from 'lib/globals';
import classNames from 'lib/classNames';
import { getIn } from 'immutable';
import PreviewContext from 'context/PreviewContext';
import { useFocusWithin } from 'react-aria';
import useRefState from 'hooks/useRefState';

const Bundles = ({ context, validationErrors, shopContext }) => {
    const { previewBundlesVariantsPicker } = useContext(PreviewContext);
    const { state: campaign, newBundle } = context;
    const [
        visiblePickerColorPickers,
        setVisiblePickerColorPickers,
        visiblePickerColorPickers_ref
    ] = useRefState([]);
    const handleColorPickerVisibleChange = name => visible => {
        if (visible) {
            const set = new Set(...visiblePickerColorPickers).add(name);
            setVisiblePickerColorPickers([...set]);
        } else {
            const v = visiblePickerColorPickers.filter(n => n !== name);
            setVisiblePickerColorPickers(v);
        }
    };
    let { focusWithinProps } = useFocusWithin({
        onFocusWithin: () => previewBundlesVariantsPicker(true, context.state.settings.bundles),
        onBlurWithin: event => {
            if (!visiblePickerColorPickers_ref.current.length) previewBundlesVariantsPicker(false);
        }
    });
    return (
        <FormGroup
            className={styles.group}
            label={CAMPAIGN_TYPES['bundles']}
            info="Create rewards for when a customer buys more of certain products."
        >
            <FormControl label="style" error={validationErrors['settings.bundles.style']}>
                <ButtonSelect
                    options={[
                        { value: 'vertical', label: 'Vertical' },
                        { value: 'horizontal', label: 'Horizontal' }
                    ]}
                    showSeparator={false}
                    BindToContext={[context, 'settings.bundles.style']}
                />
            </FormControl>
            <FormControl label="Create offer" marginBottom="mb-0">
                <div className={classNames('flex flex-col')}>
                    {campaign.settings.bundles.list.map((bundle, index) =>
                        <Bundle
                            bundle={bundle}
                            key={index}
                            index={index}
                            context={context}
                            shopContext={shopContext}
                        />
                    )}
                </div>
            </FormControl>
            <div className={classNames('flex justify-end mb-4')}>
                <Button text="New offer" icon="plus" onClick={() => newBundle()} type="info" />
            </div>
            <Summary campaign={context.state} />
            <FormControl
                label="offer description"
                error={validationErrors['settings.bundles.text']}
            >
                <Input
                    BindToContext={[context, 'settings.bundles.text']}
                    placeholder="offer description"
                />
            </FormControl>
            <FormControl label="pricing" error={validationErrors['settings.bundles.prefix']}>
                <Input BindToContext={[context, 'settings.bundles.prefix']} placeholder="pricing" />
            </FormControl>
            <FormControl label="font size" error={validationErrors['settings.bundles.font_size']}>
                <Input
                    BindToContext={[context, 'settings.bundles.font_size']}
                    type="number"
                    min={0.5}
                    max={2}
                    step={0.1}
                    placeholder="font size"
                />
            </FormControl>
            <FormControl
                label="Offer colors"
                error={
                    validationErrors['settings.bundles.background'] ||
                    validationErrors['settings.bundles.color']
                }
            >
                <ColorPicker
                    text="Background"
                    BindToContext={[context, 'settings.bundles.background']}
                />
                <ColorPicker
                    text="Color"
                    position="bottom right"
                    BindToContext={[context, 'settings.bundles.color']}
                />
            </FormControl>
            <FormControl
                label="badge colors"
                error={
                    validationErrors['settings.bundles.badge.text'] ||
                    validationErrors['settings.bundles.badge.background'] ||
                    validationErrors['settings.bundles.badge.text']
                }
            >
                <ColorPicker
                    text="text"
                    BindToContext={[context, 'settings.bundles.badge.text']}
                />
                <ColorPicker
                    text="Background"
                    BindToContext={[context, 'settings.bundles.badge.background']}
                />
                <ColorPicker
                    text="board"
                    BindToContext={[context, 'settings.bundles.badge.border']}
                />
            </FormControl> 
            <FormControl
                beta
                label="variant picker"
                info="Appears when product has variants and customer clicks volume increase buttons in cart."
                withSwitch={[context, 'settings.bundles.variantsPicker.active']}
            >
                <span />
            </FormControl>
            <div
                className={classNames({
                    hidden: !context.state.settings.bundles.variantsPicker?.active
                })}
                {...focusWithinProps}
            >
                <FormControl
                    label="Multi Product Label"
                    className="mt-5"
                    info="Appears when customer has to add more than 1 product with a variant."
                >
                    <Input
                        placeholder="Multi Product Label"
                        BindToContext={[
                            context,
                            'settings.bundles.variantsPicker.label',
                            value => {
                                const { bundles } = context.state.settings;
                                bundles.variantsPicker.label = value;
                                previewBundlesVariantsPicker(true, bundles);
                            }
                        ]}
                    />
                </FormControl>
                <FormControl label="Confirm Product Label">
                    <Input
                        placeholder="Confirm Product Label"
                        BindToContext={[
                            context,
                            'settings.bundles.variantsPicker.confirm',
                            value => {
                                const { bundles } = context.state.settings;
                                bundles.variantsPicker.confirm = value;
                                previewBundlesVariantsPicker(true, campaign.settings.bundles);
                            }
                        ]}
                    />
                </FormControl>
                <FormControl
                    label="Picker colors"
                    isSingleCol
                    error={validationErrors['settings.bundles.variantsPicker.textColor']}
                >
                    <ColorPicker
                        text="Text"
                        BindToContext={[
                            context,
                            'settings.bundles.variantsPicker.textColor',
                            value => {
                                const { bundles } = context.state.settings;
                                bundles.variantsPicker.textColor = value;
                                previewBundlesVariantsPicker(true, campaign.settings.bundles);
                            }
                        ]}
                        onVisibleChange={handleColorPickerVisibleChange('textColor')}
                    />
                    <div className={classNames('flex')}>
                        <ColorPicker
                            className="py-3 w-1/2"
                            text="Dropdown text"
                            BindToContext={[
                                context,
                                'settings.bundles.variantsPicker.dropdownTextColor',
                                value => {
                                    const { bundles } = context.state.settings;
                                    bundles.variantsPicker.dropdownTextColor = value;
                                    previewBundlesVariantsPicker(true, campaign.settings.bundles);
                                }
                            ]}
                            onVisibleChange={handleColorPickerVisibleChange('dropdownTextColor')}
                        />
                        <ColorPicker
                            className="w-1/2"
                            text="Dropdown background"
                            BindToContext={[
                                context,
                                'settings.bundles.variantsPicker.dropdownBackground',
                                value => {
                                    const { bundles } = context.state.settings;
                                    bundles.variantsPicker.dropdownBackground = value;
                                    previewBundlesVariantsPicker(true, campaign.settings.bundles);
                                }
                            ]}
                            onVisibleChange={handleColorPickerVisibleChange('dropdownBackground')}
                        />
                    </div>
                    <div className={classNames('flex')}>
                        <ColorPicker
                            className="py-3 w-1/2"
                            text="Confirm Button Background"
                            BindToContext={[
                                context,
                                'settings.bundles.variantsPicker.confirmBackground',
                                value => {
                                    const { bundles } = context.state.settings;
                                    bundles.variantsPicker.confirmBackground = value;
                                    previewBundlesVariantsPicker(true, campaign.settings.bundles);
                                }
                            ]}
                            onVisibleChange={handleColorPickerVisibleChange('confirmBackground')}
                        />
                        <ColorPicker
                            className="w-1/2"
                            text="Confirm Button Text"
                            BindToContext={[
                                context,
                                'settings.bundles.variantsPicker.confirmTextColor',
                                value => {
                                    const { bundles } = context.state.settings;
                                    bundles.variantsPicker.confirmTextColor = value;
                                    previewBundlesVariantsPicker(true, campaign.settings.bundles);
                                }
                            ]}
                            onVisibleChange={handleColorPickerVisibleChange('confirmTextColor')}
                        />
                    </div>
                </FormControl>
            </div>
            {/* <FormControl label="discount title" error={validationErrors['settings.applied_discount_title']}>
                <Input placeholder="discount title" BindToContext={[context, 'settings.applied_discount_title']} />
            </FormControl> */}
        </FormGroup>
    );
};

export default Bundles;

const Summary = ({ campaign }) => {
    const { target, settings: { bundles } } = campaign;
    //TODO: i need show money formatting here
    if ((target.type === 'specific_products' && !target.selected.length) || !bundles.list.length)
        return null;
    const must_truncated = text => text.length > 25;
    const truncate_name = text =>
        `"${text.substring(0, 25).trim()}${must_truncated(text) ? '...' : ''}"`;
    const product_name =
        target.type === 'specific_products'
            ? truncate_name(target.selected[0].title)
            : 'any product';

    return (
        <Alert
            type="info"
            icon="info-circle"
            title="campaign summary"
            className={classNames('mb-6')}
        >
            {bundles.list.map(({ quantity, type, reward }, i) => {
                if (!quantity || !type || !reward) return null;
                const isLast = i === bundles.list.length - 1;
                const formatted_reward = type === 'percentage' ? `${reward}%` : `$${reward}`;
                return (
                    <p key={i}>
                        {`Buy ${quantity} ${isLast
                            ? ' or more'
                            : ''} of ${product_name} and get ${formatted_reward} off.`}
                    </p>
                );
            })}
        </Alert>
    );
};
